import { useEffect, useMemo, useState } from 'react'
import { Wand2 } from 'lucide-react'
import PlayerTypeahead from '../PlayerTypeahead'
import { Button } from '../ui/button'
import { useNotification } from '../../contexts/NotificationContext'
import { seedingOrder } from '../../lib/bracketSeeding'

const BRACKET_SIZES = [4, 8, 16, 32, 64]
const TBD_PLAYER = { id: 'TBD', name: 'TBD', gender: null }

function makeEmptyRosterEntry(isTeam, teamSize) {
  if (isTeam) return Array.from({ length: teamSize }, () => null)
  return null
}

function makeEmptyRoster(size, isTeam, teamSize) {
  return Array.from({ length: size }, () => makeEmptyRosterEntry(isTeam, teamSize))
}

function rosterEntryToMatchPlayer(entry, isTeam, teamSize) {
  if (isTeam) {
    const members = Array.isArray(entry) ? entry : []
    return Array.from({ length: teamSize }, (_, i) => members[i] || { ...TBD_PLAYER })
  }
  return entry || { ...TBD_PLAYER }
}

function rosterEntryIsEmpty(entry, isTeam) {
  if (isTeam) return !Array.isArray(entry) || entry.every((m) => !m)
  return !entry
}

function RosterRow({
  seed,
  entry,
  isTeam,
  teamSize,
  isMixed,
  divisionGender,
  sportId,
  excludePlayerIds,
  onSingleChange,
  onTeamMemberChange,
}) {
  if (isTeam) {
    const members = Array.isArray(entry)
      ? entry
      : Array.from({ length: teamSize }, () => null)
    return (
      <div className="flex items-start gap-3 rounded-md border p-2">
        <div className="w-10 shrink-0 pt-2 text-center text-sm font-mono text-muted-foreground">
          {seed}
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Array.from({ length: teamSize }).map((__, mi) => {
            const label = isMixed
              ? mi === 0
                ? 'Female'
                : 'Male'
              : `Player ${mi + 1}`
            const playerPosition = isMixed ? mi + 1 : null
            return (
              <div key={mi}>
                <label className="text-xs text-muted-foreground mb-1 block">
                  {label}
                </label>
                <PlayerTypeahead
                  value={members[mi]}
                  onChange={(v) => onTeamMemberChange(mi, v)}
                  placeholder="Enter player name..."
                  divisionGender={divisionGender}
                  divisionTeamSize={teamSize}
                  playerPosition={playerPosition}
                  excludePlayerIds={excludePlayerIds}
                  sportId={sportId}
                />
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 shrink-0 text-center text-sm font-mono text-muted-foreground">
        {seed}
      </div>
      <div className="flex-1">
        <PlayerTypeahead
          value={entry}
          onChange={onSingleChange}
          placeholder="Enter player name..."
          divisionGender={divisionGender}
          divisionTeamSize={1}
          excludePlayerIds={excludePlayerIds}
          sportId={sportId}
        />
      </div>
    </div>
  )
}

export default function BracketQuickSetup({
  matches,
  bracket,
  onReplaceMatches,
}) {
  const notify = useNotification()
  const isTeam = bracket?.divisionTeamSize > 1
  const teamSize = bracket?.divisionTeamSize || 1
  const divisionGender = bracket?.divisionGender || null
  const sportId = bracket?.sportId || null
  const isMixed = isTeam && divisionGender === 'mixed'

  const [size, setSize] = useState(8)
  const [roster, setRoster] = useState(() => makeEmptyRoster(8, isTeam, teamSize))

  // Resize roster when size or team shape changes, preserving existing entries
  useEffect(() => {
    setRoster((prev) =>
      Array.from({ length: size }, (_, i) => {
        const existing = prev[i]
        if (isTeam) {
          if (Array.isArray(existing)) {
            const members = existing.slice(0, teamSize)
            while (members.length < teamSize) members.push(null)
            return members
          }
          return makeEmptyRosterEntry(isTeam, teamSize)
        }
        return Array.isArray(existing) ? null : existing ?? null
      })
    )
  }, [size, isTeam, teamSize])

  const setRosterSingle = (rowIndex, value) => {
    setRoster((prev) => {
      const next = [...prev]
      next[rowIndex] = value
      return next
    })
  }

  const setRosterTeamMember = (rowIndex, memberIndex, value) => {
    setRoster((prev) => {
      const next = [...prev]
      const team = Array.isArray(next[rowIndex])
        ? [...next[rowIndex]]
        : Array.from({ length: teamSize }, () => null)
      team[memberIndex] = value
      next[rowIndex] = team
      return next
    })
  }

  const usedPlayerIds = useMemo(() => {
    const ids = []
    roster.forEach((entry) => {
      if (isTeam && Array.isArray(entry)) {
        entry.forEach((m) => {
          if (m?.id && m.id !== 'TBD') ids.push(m.id)
        })
      } else if (entry?.id && entry.id !== 'TBD') {
        ids.push(entry.id)
      }
    })
    return ids
  }, [roster, isTeam])

  const filledCount = roster.filter((e) => !rosterEntryIsEmpty(e, isTeam)).length

  const handleGenerate = async () => {
    if (matches.length > 0) {
      const confirmed = await notify.confirm({
        title: 'Replace existing matches?',
        description:
          'Generating a bracket will replace all existing matches. This cannot be undone until you leave without saving.',
        confirmText: 'Replace matches',
        cancelText: 'Cancel',
        variant: 'warning',
      })
      if (!confirmed) return
    }

    const order = seedingOrder(size)
    const numMatches = size / 2
    const baseId = Date.now()
    const newMatches = []

    for (let i = 0; i < numMatches; i++) {
      const seed1 = order[i * 2]
      const seed2 = order[i * 2 + 1]
      const p1Entry = roster[seed1 - 1]
      const p2Entry = roster[seed2 - 1]
      newMatches.push({
        id: baseId + i,
        round: 1,
        matchNumber: i + 1,
        player1: rosterEntryToMatchPlayer(p1Entry, isTeam, teamSize),
        player1Seed: String(seed1),
        player2: rosterEntryToMatchPlayer(p2Entry, isTeam, teamSize),
        player2Seed: String(seed2),
        nextRound: 2,
        nextMatchNumber: Math.ceil((i + 1) / 2),
      })
    }

    onReplaceMatches(newMatches)
    notify.success(
      `Generated ${numMatches} round-1 matches. Switch to List or Outline view to fine-tune.`
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Quick Setup</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Enter your seeded roster below and click Generate to build a standard
          bracket. Fine-tune individual matches afterward in the List or
          Outline view.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm font-medium">Seeds:</span>
        <div className="flex gap-1" role="radiogroup" aria-label="Bracket size">
          {BRACKET_SIZES.map((n) => (
            <Button
              key={n}
              variant={size === n ? 'primaryNeon' : 'outline'}
              size="sm"
              onClick={() => setSize(n)}
              role="radio"
              aria-checked={size === n}
            >
              {n}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-2 mb-6">
        {Array.from({ length: size }).map((_, i) => (
          <RosterRow
            key={i}
            seed={i + 1}
            entry={roster[i]}
            isTeam={isTeam}
            teamSize={teamSize}
            isMixed={isMixed}
            divisionGender={divisionGender}
            sportId={sportId}
            excludePlayerIds={usedPlayerIds}
            onSingleChange={(v) => setRosterSingle(i, v)}
            onTeamMemberChange={(mi, v) => setRosterTeamMember(i, mi, v)}
          />
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="text-sm text-muted-foreground">
          {filledCount} of {size} seeds filled. Empty seeds become byes (TBD).
        </p>
        <Button onClick={handleGenerate} variant="primaryNeon" size="lg" className="w-full sm:w-auto">
          <Wand2 className="h-4 w-4 mr-2" />
          Generate Bracket
        </Button>
      </div>
    </div>
  )
}
